import React from "react";
import { Building2, ChevronDown, Plus } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import WorkspaceSection from "@/components/sidebar/WorkspaceSection";
import { Workspace, WorkspaceCompany } from "@/types/workspace";
import { useLocation } from "react-router-dom";
import DomainFavicon from "@/components/ui/DomainFavicon";

interface WorkspaceCompanyDropdownProps {
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  activeCompanyId: number | null;
  loading: boolean;
  onCompanyClick: (workspace: Workspace, company: WorkspaceCompany) => void;
  onAddCompany: (workspace?: Workspace) => void;
  onAddReport: (company: WorkspaceCompany | null) => void;
  onAddWorkspace: () => void;
  collapsed?: boolean;
}

const getWorkspaceInitials = (workspace: Workspace | null): string => {
  if (!workspace?.name) {
    return "WS";
  }

  const words = workspace.name.split(" ").filter(Boolean);
  if (words.length === 0) {
    return "WS";
  }
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

const findActiveCompany = (
  workspaces: Workspace[],
  activeCompanyId: number | null,
): WorkspaceCompany | null => {
  if (!activeCompanyId) return null;
  for (const ws of workspaces) {
    const found = ws.companies.find((c) => c.company_id === activeCompanyId);
    if (found) return found;
  }
  return null;
};

const WorkspaceCompanyDropdown: React.FC<WorkspaceCompanyDropdownProps> = ({
  workspaces,
  activeWorkspace,
  activeCompanyId,
  loading,
  onCompanyClick,
  onAddCompany,
  onAddReport,
  onAddWorkspace,
  collapsed = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();
  const activeCompany = findActiveCompany(workspaces, activeCompanyId);

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div
        className={[
          "mb-3 animate-pulse rounded-md border border-[color:var(--vb-line)] bg-[var(--vb-bg-subtle)]",
          collapsed ? "h-10 w-10" : "h-11 w-full",
        ].join(" ")}
      />
    );
  }

  const hasNoWorkspaces = workspaces.length === 0;

  const handleCompanyClick = (workspace: Workspace, company: WorkspaceCompany) => {
    onCompanyClick(workspace, company);
    setOpen(false);
  };

  const handleAddCompany = (workspace?: Workspace) => {
    onAddCompany(workspace);
    setOpen(false);
  };

  const handleAddWorkspace = () => {
    onAddWorkspace();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div
        className={[
          "mb-3 flex gap-1.5",
          collapsed ? "flex-col items-center" : "flex-row items-center",
        ].join(" ")}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={[
              // Keep the trigger a light control on hover/open (just a border
              // highlight) — the outline variant's default flips it to a solid
              // brand fill, which leaves the dark label text unreadable.
              "border-[color:var(--vb-line)]",
              "hover:bg-[var(--vb-surface)] hover:text-[color:var(--vb-text)] hover:border-[color:var(--vb-line-strong)]",
              "data-[state=open]:border-[color:var(--vb-brand)]",
              collapsed
                ? "h-10 w-10 p-0"
                : "h-11 min-w-0 flex-1 justify-between px-2",
            ].join(" ")}
          >
            <span className="flex min-w-0 items-center gap-2">
              {activeCompany ? (
                <DomainFavicon
                  name={activeCompany.company_name}
                  url={activeCompany.company_url}
                  size={28}
                  className="h-7 w-7 shrink-0"
                />
              ) : (
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[var(--vb-bg-subtle)] text-xs font-semibold text-[color:var(--vb-text-2)]">
                  {getWorkspaceInitials(activeWorkspace)}
                </span>
              )}
              {!collapsed ? (
                <span className="truncate text-sm font-semibold text-[color:var(--vb-text)]">
                  {activeCompany
                    ? activeCompany.company_name
                    : (activeWorkspace?.name ?? "Select a company")}
                </span>
              ) : null}
            </span>
            {!collapsed ? <ChevronDown className="h-4 w-4 text-[color:var(--vb-text-2)]" /> : null}
          </Button>
        </PopoverTrigger>

        {/* "Run a new report" — opens the report dialog seeded with the company
            currently shown in the selector (falls back to a blank form when
            none is selected). Paired with the selector as a light outline
            control; the same hover override keeps it from flipping to a solid
            brand fill. */}
        <Button
          type="button"
          variant="outline"
          onClick={() => onAddReport(activeCompany)}
          title="Run a new report"
          aria-label="Run a new report"
          className={[
            "shrink-0 border-[color:var(--vb-line)] text-[color:var(--vb-text-2)]",
            "hover:bg-[var(--vb-surface)] hover:text-[color:var(--vb-text)] hover:border-[color:var(--vb-line-strong)]",
            collapsed ? "h-10 w-10 p-0" : "h-11 w-11 p-0",
          ].join(" ")}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <PopoverContent
        align={collapsed ? "start" : "start"}
        side={collapsed ? "right" : "bottom"}
        className={[
          "rounded-lg border border-[color:var(--vb-line-strong)] bg-[var(--vb-surface)] p-2",
          collapsed ? "w-[260px]" : "w-[236px] max-w-[236px]",
        ].join(" ")}
      >
        {hasNoWorkspaces ? (
          <div className="rounded-md bg-[var(--vb-fail-bg)] p-3 text-sm text-[color:var(--vb-fail-ink)]">
            Could not resolve any workspaces.
          </div>
        ) : (
          <div
            className="max-h-[420px] space-y-3 overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[var(--vb-surface)] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--vb-line-strong)] hover:[&::-webkit-scrollbar-thumb]:bg-[var(--vb-text-3)]"
            style={{ scrollbarColor: "var(--vb-line-strong) var(--vb-surface)" }}
          >
            {workspaces.map((workspace) => (
              <WorkspaceSection
                key={workspace.id}
                workspace={workspace}
                activeCompanyId={activeCompanyId}
                onCompanyClick={handleCompanyClick}
              />
            ))}
          </div>
        )}

        <div className="mt-3 border-t border-[color:var(--vb-line)] pt-2">
          <div className="flex flex-col gap-1.5">
            <Button
              type="button"
              variant="ghost"
              className="h-8 w-full justify-start px-2 text-xs font-medium text-accent"
              onClick={() => handleAddCompany(activeWorkspace ?? undefined)}
            >
              <Plus className="h-4 w-4" />
              Add a company
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="h-8 w-full justify-start px-2 text-xs font-medium text-accent"
              onClick={handleAddWorkspace}
            >
              <Building2 className="h-4 w-4" />
              Add a workspace
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default WorkspaceCompanyDropdown;
